/**
 * Completed-item visibility window — single source of truth.
 *
 * On the live boards (the doctor's Smart Workflow procedures + the lab
 * technician's queue) a COMPLETED / terminal item stays visible for a few days
 * then drops off, so the board doesn't accumulate month-old work. The full
 * history is always available on the patient's visit page ("My Visit").
 * Incomplete items are never capped — they always show until they're done.
 */
export const COMPLETED_VISIBLE_DAYS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

// Best available "when did this reach its terminal state" timestamp.
function terminalRefMs(item) {
  const ts = item?.completed_at || item?.updated_at || item?.created_at;
  const t = ts ? Date.parse(ts) : NaN;
  return Number.isNaN(t) ? null : t;
}

/**
 * True when a terminal/completed item is recent enough to stay on a live board.
 * Unknown age (no usable timestamp) → kept, so missing data never hides real
 * recent work.
 * @param {object} item   - a sitting or lab order (reads completed_at/updated_at/created_at)
 * @param {number} [days] - window in days (default COMPLETED_VISIBLE_DAYS)
 * @returns {boolean}
 */
export function isRecentlyCompleted(item, days = COMPLETED_VISIBLE_DAYS) {
  const t = terminalRefMs(item);
  if (t == null) return true;
  return (Date.now() - t) <= days * DAY_MS;
}
